import {
  forwardRef,
  HTMLInputAutoCompleteAttribute,
  useCallback,
  useEffect,
  useState,
} from 'react';

export interface InputProps {
  /** Input id */
  id?: string;
  /** How large should the input be? */
  size?: 'small' | 'medium' | 'large';
  /** Type of input */
  type?:
    | 'text'
    | 'number'
    | 'email'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'password'
    | 'textarea'
    | 'search';
  /** Name */
  name?: string;
  /** Value */
  value?: string;
  /** Placeholder */
  placeholder?: string;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is valid */
  valid?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is required */
  required?: boolean;
  /** Optional on change handler */
  onChange?: (value: string) => void;
  /** Optional on search handler */
  onSearch?: (value: string) => void;
  /** Additional class */
  className?: string;
  /** max */
  max?: string;
  /** min */
  min?: string;
  /** autocomplete */
  autoComplete?: HTMLInputAutoCompleteAttribute;
  /** rows (for textarea only) */
  rows?: number;
  /** onFocus event handler */
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  /** onBlur event handler */
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

/** An form input */
export const Input = forwardRef(
  (
    {
      id,
      size = 'medium',
      type = 'text',
      name = '',
      value,
      placeholder,
      error,
      valid,
      disabled,
      onChange,
      onSearch,
      className,
      ...props
    }: InputProps,
    ref: any,
  ) => {
    const [isError, setError] = useState(error);
    const [isValid, setValid] = useState(valid);
    const [isDisabled, setDisabled] = useState(disabled);
    const [currentValue, setValue] = useState(value ?? '');

    useEffect(() => {
      if (typeof error !== 'undefined') {
        setError(error);
      }
    }, [error]);

    useEffect(() => {
      if (typeof valid !== 'undefined') {
        setValid(valid);
      }
    }, [valid]);

    useEffect(() => {
      if (typeof disabled !== 'undefined') {
        setDisabled(disabled);
      }
    }, [disabled]);

    useEffect(() => {
      if (typeof value !== 'undefined') {
        setValue(value);
      }
    }, [value]);

    const handleSearch = useCallback(
      (e: Event) => {
        const input = e.target as HTMLInputElement;
        const val = input.value;

        setValue(val);
        onChange?.(val);
        onSearch?.(val);
      },
      [onChange, onSearch],
    );

    useEffect(() => {
      if (type !== 'search' || !onSearch || !ref.current) return;
      const input = ref.current as HTMLInputElement;

      input.addEventListener('search', handleSearch);

      return () => {
        if (input) {
          input.removeEventListener('search', handleSearch);
        }
      };
    }, [type, onSearch, handleSearch, ref]);

    return (
      <div
        className={[
          'input',
          `input--${size}`,
          `input--${type}`,
          className,
        ].join(' ')}
        data-valid={(isValid && '1') || null}
        data-error={(isError && '1') || null}
      >
        {type !== 'textarea' && (
          <input
            id={id}
            ref={ref}
            className="input__field"
            type={type}
            name={name}
            value={currentValue}
            placeholder={placeholder}
            disabled={isDisabled ? true : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValue(e.target.value);
              if (onChange) {
                onChange(e.target.value);
              }
            }}
            {...props}
          />
        )}
        {type === 'textarea' && (
          <textarea
            id={id}
            ref={ref}
            className="input__field input__field--textarea"
            name={name}
            value={currentValue}
            placeholder={placeholder}
            disabled={isDisabled ? true : undefined}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setValue(e.target.value);
              if (onChange) {
                onChange(e.target.value);
              }
            }}
            {...props}
          />
        )}
      </div>
    );
  },
);
